import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import Login from './components/Login';
import OAuthCallback from './components/OAuthCallback';
import Navigation from './components/Navigation';
import Profile from './components/Profile';
import PublicProfile from './components/PublicProfile';
import ServerDetails from './components/ServerDetails';
import Library from './components/Library';
import ProtectedLayout from './components/ProtectedLayout';
import Home from './components/Home';
import ToastContainer from './components/ToastContainer';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { ThemeProvider } from './contexts/ThemeContext';
import Loading from './components/Loading';
import CreateServer from './components/CreateServer';
import EditServer from './components/EditServer';
import AdminPanel from './components/AdminPanel';
import AdminServerForm from './components/AdminServerForm';
import AdminServers from './components/AdminServers';
import AdminUsers from './components/AdminUsers';
import AdminComments from './components/AdminComments';
import AdminStatistics from './components/AdminStatistics';
import AdminSettings from './components/AdminSettings';
import AdminLogs from './components/AdminLogs';
import AdminAds from './components/AdminAds';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';


// Protected route component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <Loading message="Loading session..." />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

const App: React.FC = () => {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <AuthProvider>
          <Router>
            <div className="min-h-screen bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-300">
              <ToastContainer />
              <Routes>
                <Route
                  path="/login"
                  element={
                    <ProtectedLayout showNavigation={false}>
                      <Login />
                    </ProtectedLayout>
                  }
                />
                <Route
                  path="/auth/callback"
                  element={<OAuthCallback />}
                />
                <Route
                  path="/"
                  element={
                    <ProtectedLayout>
                      <Home />
                    </ProtectedLayout>
                  }
                />
                <Route
                  path="/server/:id"
                  element={
                    <ProtectedLayout>
                      <ServerDetails />
                    </ProtectedLayout>
                  }
                />
                <Route
                  path="/user/:identifier"
                  element={
                    <ProtectedLayout>
                      <PublicProfile />
                    </ProtectedLayout>
                  }
                />
                <Route
                  path="/library"
                  element={
                    <ProtectedRoute>
                      <ProtectedLayout>
                        <Library />
                      </ProtectedLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/profile"
                  element={
                    <ProtectedRoute>
                      <ProtectedLayout>
                        <Profile />
                      </ProtectedLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/servers/new"
                  element={
                    <ProtectedRoute>
                      <ProtectedLayout>
                        <CreateServer />
                      </ProtectedLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/servers/:id/edit"
                  element={
                    <ProtectedRoute>
                      <ProtectedLayout>
                        <EditServer />
                      </ProtectedLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute>
                      <ProtectedLayout>
                        <AdminPanel />
                      </ProtectedLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/servers/new"
                  element={
                    <ProtectedRoute>
                      <ProtectedLayout>
                        <AdminServerForm />
                      </ProtectedLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/servers"
                  element={
                    <ProtectedRoute>
                      <ProtectedLayout>
                        <AdminServers />
                      </ProtectedLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/users"
                  element={
                    <ProtectedRoute>
                      <ProtectedLayout>
                        <AdminUsers />
                      </ProtectedLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/comments"
                  element={
                    <ProtectedRoute>
                      <ProtectedLayout>
                        <AdminComments />
                      </ProtectedLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/statistics"
                  element={
                    <ProtectedRoute>
                      <ProtectedLayout>
                        <AdminStatistics />
                      </ProtectedLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/settings"
                  element={
                    <ProtectedRoute>
                      <ProtectedLayout>
                        <AdminSettings />
                      </ProtectedLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/ads"
                  element={
                    <ProtectedRoute>
                      <ProtectedLayout>
                        <AdminAds />
                      </ProtectedLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/logs"
                  element={
                    <ProtectedRoute>
                      <ProtectedLayout>
                        <AdminLogs />
                      </ProtectedLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/privacy"
                  element={
                    <ProtectedLayout>
                      <PrivacyPolicy />
                    </ProtectedLayout>
                  }
                />
                <Route
                  path="/terms"
                  element={
                    <ProtectedLayout>
                      <TermsOfService />
                    </ProtectedLayout>
                  }
                />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </div>
          </Router>
        </AuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
};

export default App;